import { useCallback, useEffect, useState } from 'react';
import { cms as cmsApi } from '../api/endpoints';
import { useToast } from '../context/ToastContext';
import { Loading, Empty, Modal, Can, dateTime } from '../components/Common';

export default function Cms() {
  const toast = useToast();
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await cmsApi.list();
      setPages(res.data.pages);
    } catch (err) {
      toast.error('Could not load pages', err.message);
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { document.title = 'CMS Pages · UrbanClean'; }, []);

  const remove = async (page) => {
    if (!window.confirm(`Delete "${page.title}"?`)) return;
    try {
      await cmsApi.remove(page._id);
      toast.success('Page deleted');
      load();
    } catch (err) {
      toast.error('Could not delete', err.message);
    }
  };

  if (loading) return <Loading label="Loading pages" />;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>CMS Pages</h1>
          <div className="sub">Terms, privacy policy, FAQ and about — read by the mobile apps</div>
        </div>
        <Can permission="cms.create">
          <button className="btn btn-blue" onClick={() => setEditing({})}>+ New page</button>
        </Can>
      </div>

      <div className="card">
        {pages.length === 0 ? <Empty title="No pages yet" /> : (
          <div className="table-wrap">
            <table className="data">
              <thead><tr><th>Page</th><th>Slug</th><th>Status</th><th>Updated</th><th></th></tr></thead>
              <tbody>
                {pages.map((p) => (
                  <tr key={p._id}>
                    <td className="strong">{p.title}</td>
                    <td className="mono muted">{p.slug}</td>
                    <td><span className={`badge badge-${p.isActive ? 'green' : 'gray'}`}>{p.isActive ? 'Live' : 'Hidden'}</span></td>
                    <td className="muted nowrap">{dateTime(p.updatedAt)}</td>
                    <td className="right nowrap">
                      <Can permission="cms.update">
                        <button className="btn btn-sm btn-ghost" onClick={() => setEditing(p)}>Edit</button>
                      </Can>
                      <Can permission="cms.delete">
                        <button className="btn btn-sm btn-ghost" style={{ marginLeft: 6, color: 'var(--red)' }} onClick={() => remove(p)}>Delete</button>
                      </Can>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <PageEditor page={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
    </>
  );
}

function PageEditor({ page, onClose, onSaved }) {
  const toast = useToast();
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!page) return;
    setForm({
      slug: page.slug || '', title: page.title || '',
      content: page.content || '', isActive: page.isActive !== false,
    });
  }, [page]);

  if (!page) return null;
  const set = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const save = async () => {
    if (!form.slug || !form.title) return toast.error('Slug and title are required');
    setBusy(true);
    try {
      if (page._id) await cmsApi.update(page._id, form);
      else await cmsApi.create(form);
      toast.success('Page saved');
      onSaved();
    } catch (err) {
      toast.error('Could not save', err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open wide title={page._id ? `Edit — ${page.title}` : 'New page'} onClose={onClose}
      footer={<>
        <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save page'}</button>
      </>}>
      <div className="row">
        <div className="field"><label>Title *</label><input className="input" value={form.title || ''} onChange={set('title')} /></div>
        <div className="field">
          <label>Slug *</label>
          <input className="input mono" value={form.slug || ''} disabled={Boolean(page._id)} onChange={set('slug')} placeholder="terms" />
          <div className="hint">The app fetches /api/cms/&lt;slug&gt;.</div>
        </div>
      </div>
      <div className="field">
        <label>Content</label>
        <textarea className="textarea" style={{ minHeight: 260 }} value={form.content || ''} onChange={set('content')} />
      </div>
      <div className="field">
        <label className="switch">
          <input type="checkbox" checked={form.isActive} onChange={set('isActive')} />
          <span className="track" /><span>Visible in the app</span>
        </label>
      </div>
    </Modal>
  );
}
